import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { api, type StreamInfo } from "../api";
import { subscribeForeground } from "./page-lifecycle";
import { channelRunState, LIVE_START_MAX_ATTEMPTS, noteLiveFailure } from "./live-start";
import { LIVE_HEAL_AFTER_MS, LIVE_HEAL_MAX, LIVE_SIBLING_WAIT_MS, planLiveQueue, type LiveQueueFact } from "./live-queue";
import { logLiveStep } from "./live-trace";
import {
  clearChannelStartCooldown,
  LIVE_VIEW_STREAM_TYPE,
  noteChannelLive,
  requestChannelStartDetail,
  requestChannelStop,
  streamStatusBatchPath,
  streamStatusPath,
} from "./stream-start";

/** After a real picture, wait this long before another 0x9101. A short muxer gap must not restart the camera. */
export const LIVE_RECOVER_AFTER_MS = 20_000;

/** Rear starts with front. One channel does not wait for the other to publish. */
export const SIBLING_PUBLISH_WAIT_MS = LIVE_SIBLING_WAIT_MS;

const liveAttempts = new Map<string, { count: number; nextAt: number }>();
const healAttempts = new Map<string, { count: number; nextAt: number }>();
/** Genuine end-to-end failures. A sent 0x9101 does not increment this. */
const liveFailures = new Map<string, { count: number; countedStart: number | null; logged: boolean }>();

function attemptKey(viewerId: string, deviceId: string, channel: number): string {
  return `${viewerId}\u0000${deviceId}\u0000${channel}`;
}

/** Channels whose player already has a frame. A status blip must not start them again. */
const playingKeys = new Set<string>();

export function noteChannelPicture(deviceId: string, channel: number, playing: boolean): void {
  if (!deviceId || channel < 1) return;
  const key = `${deviceId}:${channel}`;
  if (playing) playingKeys.add(key);
  else playingKeys.delete(key);
}

export function channelHasPicture(deviceId: string, channel: number): boolean {
  return playingKeys.has(`${deviceId}:${channel}`);
}

/** Manual Retry. Clears the 3-attempt cap for this viewer and channel only. */
export function resetLiveAttempts(viewerId: string, deviceId: string, channel: number): void {
  const key = attemptKey(viewerId, deviceId, channel);
  liveAttempts.delete(key);
  healAttempts.delete(key);
  liveFailures.delete(key);
}

export interface LiveWatchTarget {
  deviceId: string;
  channel: number;
  online: boolean;
  /** False while the card is offscreen. Still queued, behind visible cards. Does not stop a stream. */
  startable?: boolean;
  /** Click to play. Admitted even when the page is already at the player cap. */
  promoted?: boolean;
}

export interface LiveQueueView {
  position: Record<string, number>;
  hold: string[];
}

export type LiveStartAction = "attach" | "recover" | "skip";

/** Front and rear start independently. A dark sibling does not hold this channel. */
export function siblingStartBlocked(input: {
  channel: number;
  siblingLive: boolean;
  /** MediaMTX ready-path list says the other channel has a publisher. */
  siblingPublishing?: boolean;
  siblingStartedAt: number | null;
  now: number;
  waitMs?: number;
}): boolean {
  void input;
  return false;
}

/**
 * One decision for Dashboard and Video Wall.
 * A channel that has never published keeps attaching (the HTTP helper debounces).
 * A channel that just published is left alone. A channel dark for LIVE_RECOVER_AFTER_MS is started again.
 */
export function shouldRequestStart(input: {
  online: boolean;
  live: boolean;
  /** Ready publisher. A dark playlist must not start this channel again. */
  publishing?: boolean;
  seenLiveAt: number | null;
  now: number;
  recoverAfterMs?: number;
}): LiveStartAction {
  if (!input.online || input.live || input.publishing) return "skip";
  const recoverAfter = input.recoverAfterMs ?? LIVE_RECOVER_AFTER_MS;
  if (input.seenLiveAt == null) return "attach";
  if (input.now - input.seenLiveAt >= recoverAfter) return "recover";
  return "skip";
}

function claimKey(deviceId: string, channel: number): string {
  return `${deviceId}:${channel}`;
}

function deviceOf(key: string): string {
  return key.slice(0, key.lastIndexOf(":"));
}

function channelOf(key: string): number {
  return Number(key.slice(key.lastIndexOf(":") + 1));
}

/**
 * Shared live-stream consumer. Each surface passes its own viewerId, so closing
 * Dashboard does not drop a Video Wall lease on the same camera (and the reverse).
 */
export function useLiveChannelWatch(opts: {
  viewerId: string;
  targets: readonly LiveWatchTarget[];
  active?: boolean;
  releaseOnHide?: boolean;
  pollMs?: number;
  refreshNonce?: number;
}): { streams: Record<string, StreamInfo>; startAtRef: MutableRefObject<Map<string, number>>; queue: LiveQueueView } {
  const { viewerId, targets, active = true, releaseOnHide = false, pollMs = 4_000, refreshNonce = 0 } = opts;
  const [streams, setStreams] = useState<Record<string, StreamInfo>>({});
  const [queue, setQueue] = useState<LiveQueueView>({ position: {}, hold: [] });
  const targetsRef = useRef(targets);
  targetsRef.current = targets;
  const activeRef = useRef(active);
  activeRef.current = active;
  const viewerRef = useRef(viewerId);
  viewerRef.current = viewerId;
  const claimedRef = useRef(new Set<string>());
  const seenLiveAtRef = useRef(new Map<string, number>());
  const startAtRef = useRef(new Map<string, number>());
  const pinnedRef = useRef(new Set<string>());
  const inflightRef = useRef(new Set<string>());
  const targetKey = targets.map((t) => `${t.deviceId}:${t.channel}:${t.online ? 1 : 0}`).join(",");
  const startKey = targets
    .map((t) => `${t.deviceId}:${t.channel}:${t.startable === false ? 0 : 1}:${t.promoted ? 1 : 0}`)
    .join(",");

  const releaseKey = (key: string, keepalive = false) => {
    if (!claimedRef.current.has(key)) return;
    claimedRef.current.delete(key);
    seenLiveAtRef.current.delete(key);
    startAtRef.current.delete(key);
    pinnedRef.current.delete(key);
    liveAttempts.delete(attemptKey(viewerRef.current, deviceOf(key), channelOf(key)));
    liveFailures.delete(attemptKey(viewerRef.current, deviceOf(key), channelOf(key)));
    void requestChannelStop(deviceOf(key), channelOf(key), { keepalive, viewerId: viewerRef.current });
  };

  const releaseAll = (keepalive = false) => {
    for (const key of [...claimedRef.current]) releaseKey(key, keepalive);
  };

  useEffect(() => {
    const next = new Set(targetKey ? targetKey.split(",").map((row) => row.split(":").slice(0, -1).join(":")) : []);
    for (const key of [...claimedRef.current]) {
      if (!next.has(key)) releaseKey(key);
    }
  }, [targetKey]);

  useEffect(() => {
    if (!releaseOnHide) return;
    const onHide = () => {
      activeRef.current = false;
    };
    const stopFg = subscribeForeground(
      () => {
        activeRef.current = active;
      },
      () => {
        activeRef.current = false;
      },
    );
    window.addEventListener("pagehide", onHide);
    return () => {
      stopFg();
      window.removeEventListener("pagehide", onHide);
    };
  }, [releaseOnHide, active]);

  useEffect(() => {
    let cancelled = false;
    let ticking = false;
    const tick = async () => {
      if (ticking || cancelled) return;
      ticking = true;
      try {
        const list = targetsRef.current;
        const onlineIds = [...new Set(list.filter((t) => t.online).map((t) => t.deviceId))];
        const entries: Array<readonly [string, StreamInfo | null]> = [];
        // One batched status read for the page. A failed batch falls back to
        // the single-device path so a lease still gets its heartbeat.
        const chunks: string[][] = [];
        for (let i = 0; i < onlineIds.length; i += 32) chunks.push(onlineIds.slice(i, i + 32));
        for (const chunk of chunks) {
          if (cancelled) return;
          try {
            const res = await api<{ devices: Record<string, StreamInfo> }>(streamStatusBatchPath(chunk, viewerRef.current));
            for (const id of chunk) entries.push([id, res.devices?.[id] ?? null]);
          } catch {
            const rows = await Promise.all(
              chunk.map(async (id) => {
                try {
                  const info = await api<StreamInfo>(streamStatusPath(id, viewerRef.current));
                  return [id, info] as const;
                } catch {
                  return [id, null] as const;
                }
              }),
            );
            entries.push(...rows);
          }
        }
        if (cancelled) return;
        setStreams((prev) => {
          const next: Record<string, StreamInfo> = {};
          for (const [id, info] of entries) {
            if (info) next[id] = info;
            else if (prev[id]) next[id] = prev[id];
          }
          return next;
        });
        const jobs: Array<Promise<unknown>> = [];
        const now = Date.now();
        const facts = new Map<string, LiveQueueFact>();
        const queueTargets = list.map((target) => ({
          deviceId: target.deviceId,
          channel: target.channel,
          online: target.online,
          visible: target.startable !== false,
          promoted: target.promoted === true,
        }));
        for (const target of list) {
          const key = claimKey(target.deviceId, target.channel);
          if (!target.online) {
            seenLiveAtRef.current.delete(key);
            pinnedRef.current.delete(key);
            continue;
          }
          const info = entries.find(([id]) => id === target.deviceId)?.[1];
          const row = info?.channels?.find((ch) => ch.channel_no === target.channel);
          const live = Boolean(row?.live);
          const picture = channelHasPicture(target.deviceId, target.channel);
          const publishing = Boolean(row?.publishing) || picture;
          if (live || publishing) {
            noteChannelLive(target.deviceId, target.channel, viewerRef.current);
            claimedRef.current.add(key);
            seenLiveAtRef.current.set(key, now);
            pinnedRef.current.add(key);
            startAtRef.current.delete(key);
            liveAttempts.delete(attemptKey(viewerRef.current, target.deviceId, target.channel));
            healAttempts.delete(attemptKey(viewerRef.current, target.deviceId, target.channel));
            liveFailures.delete(attemptKey(viewerRef.current, target.deviceId, target.channel));
          }
          const startedAt = startAtRef.current.get(key) ?? null;
          const run = channelRunState({
            deviceOnline: true,
            live,
            publishing,
            startedAt,
            now,
            phase: row?.liveStart?.phase,
          });
          const aKey = attemptKey(viewerRef.current, target.deviceId, target.channel);
          const priorFail = liveFailures.get(aKey) ?? { count: 0, countedStart: null, logged: false };
          const failure = noteLiveFailure({
            count: priorFail.count,
            countedStart: priorFail.countedStart,
            startedAt,
            failed: run.state === "failed" && !publishing && !live,
          });
          if (failure.count !== priorFail.count || failure.countedStart !== priorFail.countedStart) {
            liveFailures.set(aKey, { count: failure.count, countedStart: failure.countedStart, logged: priorFail.logged });
          }
          if (failure.justExhausted && !priorFail.logged) {
            liveFailures.set(aKey, { count: failure.count, countedStart: failure.countedStart, logged: true });
            healAttempts.set(aKey, { count: LIVE_HEAL_MAX, nextAt: Number.POSITIVE_INFINITY });
            logLiveStep({
              vehicleId: target.deviceId,
              channel: target.channel,
              step: "start_exhausted",
              error: run.message || "no_media",
              durationMs: startedAt != null ? now - startedAt : undefined,
            });
          }
          const heal = healAttempts.get(aKey);
          let healReady = false;
          const giveUp = (liveFailures.get(aKey)?.count ?? 0) >= LIVE_START_MAX_ATTEMPTS;
          if (!giveUp && run.state === "failed" && !publishing && !live) {
            if (!heal) healAttempts.set(aKey, { count: 0, nextAt: now + LIVE_HEAL_AFTER_MS });
            else if (heal.count < LIVE_HEAL_MAX && now >= heal.nextAt) healReady = true;
          }
          const seen = seenLiveAtRef.current.get(key) ?? null;
          const dead =
            pinnedRef.current.has(key) &&
            !live &&
            !publishing &&
            seen != null &&
            now - seen >= LIVE_RECOVER_AFTER_MS;
          facts.set(key, {
            live,
            publishing,
            pinned: pinnedRef.current.has(key) && !dead,
            startedAt,
            failed: run.state === "failed" && !healReady && !dead,
            healReady: healReady || dead,
            inflight: inflightRef.current.has(key),
          });
        }
        const plan = planLiveQueue({ targets: queueTargets, facts, now });
        setQueue({ position: plan.position, hold: plan.hold });
        // Hidden tab keeps the leases (poll above) and does not send 0x9101 or 0x9102.
        if (!activeRef.current) return;
        for (const item of plan.start) {
          const key = claimKey(item.deviceId, item.channel);
          if (inflightRef.current.has(key)) continue;
          const fact = facts.get(key);
          const action = shouldRequestStart({
            online: true,
            live: Boolean(fact?.live),
            publishing: Boolean(fact?.publishing || fact?.pinned),
            seenLiveAt: fact?.healReady ? null : (seenLiveAtRef.current.get(key) ?? null),
            now,
          });
          if (action === "skip") continue;
          if (action === "recover") {
            seenLiveAtRef.current.set(key, now);
            clearChannelStartCooldown(item.deviceId, item.channel, viewerRef.current);
          }
          const aKey = attemptKey(viewerRef.current, item.deviceId, item.channel);
          if (fact?.healReady) {
            seenLiveAtRef.current.set(key, now);
            const prev = healAttempts.get(aKey) ?? { count: 0, nextAt: now };
            healAttempts.set(aKey, { count: prev.count + 1, nextAt: now + LIVE_HEAL_AFTER_MS });
          }
          inflightRef.current.add(key);
          // Claim before the POST returns so Next/Prev can stop this upload
          // even while the request is still in flight.
          claimedRef.current.add(key);
          const viewerId = viewerRef.current;
          const stillOnPage = () =>
            targetsRef.current.some((target) => claimKey(target.deviceId, target.channel) === key);
          jobs.push(
            requestChannelStartDetail(item.deviceId, item.channel, viewerId, LIVE_VIEW_STREAM_TYPE).then((result) => {
              inflightRef.current.delete(key);
              if (!stillOnPage()) {
                releaseKey(key);
                return;
              }
              if (!result.ok) {
                claimedRef.current.delete(key);
              }
              if (result.mode === "0x9101") startAtRef.current.set(key, Date.now());
              if (result.mode === "0x9101") {
                logLiveStep({
                  vehicleId: item.deviceId,
                  channel: item.channel,
                  step: "start_sent",
                  durationMs: Date.now() - now,
                });
                return;
              }
              if (result.mode !== "error") return;
              logLiveStep({
                vehicleId: item.deviceId,
                channel: item.channel,
                step: "START_REQUEST",
                error: "request_failed",
                durationMs: Date.now() - now,
              });
            }),
          );
        }
        // Status for the other tiles must not wait on the slowest start.
        if (jobs.length > 0) void Promise.all(jobs);
      } finally {
        ticking = false;
      }
    };
    void tick();
    // One poll for the whole page. Each card reads this result; it does not start its own loop.
    const timer = window.setInterval(() => void tick(), active ? pollMs : 15_000);
    const stop = subscribeForeground(() => {
      if (activeRef.current) void tick();
    });
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      stop();
    };
  }, [targetKey, startKey, viewerId, active, pollMs, refreshNonce]);

  useEffect(() => {
    return () => releaseAll(false);
  }, [viewerId]);

  return { streams, startAtRef, queue };
}
